import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../../../../contexts/ThemeContext';
import { useAuth } from '../../../../contexts/AuthContext';
import { cn } from '../../../../lib/utils';
import { getPrimaryContactPoint } from '../../../address-book/contactPoints';
import type { ClientSummary } from '../../useData';
import { moduleConfig } from '../../config';

interface ClientQuickActionsProps {
  clientSummary: ClientSummary;
}

interface QuickAction {
  key: string;
  label: string;
  description: string;
  permission?: { module: string; action: 'view' | 'edit' };
  onClick?: () => void;
  to?: string;
  icon: React.ReactNode;
}

const CalendarIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const MailIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
  </svg>
);

const EditIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const ClientQuickActions: React.FC<ClientQuickActionsProps> = ({ clientSummary }) => {
  const { colors } = useTheme();
  const { can } = useAuth();
  const navigate = useNavigate();
  const clientId = clientSummary.clientId;

  const primaryEmail = getPrimaryContactPoint(clientSummary.contactPoints, 'EMAIL')?.value;

  const actions: QuickAction[] = [
    {
      key: 'compose',
      label: 'Compose email',
      description: primaryEmail ? `To ${primaryEmail}` : 'Start a new message',
      permission: { module: 'emails', action: 'edit' },
      onClick: () => {
        navigate('/emails/compose', {
          state: primaryEmail ? { initialTo: primaryEmail } : undefined,
        });
      },
      icon: <MailIcon />,
    },
    {
      key: 'appointment',
      label: 'Schedule',
      description: 'New appointment',
      permission: { module: 'appointments', action: 'edit' },
      to: `/appointments/new?clientId=${clientId}`,
      icon: <CalendarIcon />,
    },
    {
      key: 'todo',
      label: 'Add todo',
      description: 'Create a follow-up task',
      permission: { module: 'todos', action: 'edit' },
      to: `/todos/new?clientId=${clientId}`,
      icon: <CheckIcon />,
    },
    {
      key: 'edit',
      label: 'Edit org',
      description: 'Update organization details',
      permission: { module: moduleConfig.module.permissionModuleKey ?? 'clients', action: 'edit' },
      to: moduleConfig.module.route.edit(clientId),
      icon: <EditIcon />,
    },
  ];

  const visible = actions.filter(
    (a) => !a.permission || can(a.permission.module, a.permission.action),
  );

  if (visible.length === 0) return null;

  return (
    <div className={cn('flex flex-wrap gap-2 mb-5', colors.border, 'pb-4 border-b')}>
      {visible.map((action) => {
        const className = cn(
          'inline-flex items-center gap-2.5 rounded-xl border px-4 py-2.5 text-sm font-medium transition-colors',
          colors.border,
          colors.background,
          'hover:opacity-90 hover:shadow-sm',
        );
        const content = (
          <>
            <span className={cn('rounded-lg p-1.5 shrink-0', colors.surfaceHover)}>{action.icon}</span>
            <span className="text-left min-w-0">
              <span className={cn('block font-semibold', colors.text)}>{action.label}</span>
              <span className={cn('block text-xs truncate', colors.textSecondary)}>{action.description}</span>
            </span>
          </>
        );

        if (action.to) {
          return (
            <Link key={action.key} to={action.to} className={className}>
              {content}
            </Link>
          );
        }

        return (
          <button key={action.key} type="button" onClick={action.onClick} className={className}>
            {content}
          </button>
        );
      })}
    </div>
  );
};

export default ClientQuickActions;
